import { useEffect, useRef, useState } from 'react';
import Icon from '../Components/Icons/Icon';
import { analyzeFaceShape } from '../lib/api';

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function normalizeFaceShape(label) {
  const s = String(label || '').toLowerCase();

  if (s.includes('round')) return 'round';
  if (s.includes('oval')) return 'oval';
  if (s.includes('straight') || s.includes('oblong') || s.includes('rectangle') || s.includes('long')) {
    return 'straight';
  }
  return 'unknown';
}

function getFrameRecommendations(shape) {
  const common = {
    note:
      'These are style suggestions to balance face proportions. Try a few frames and choose the one that feels most comfortable.',
  };

  if (shape === 'round') {
    return {
      ...common,
      headline: 'Best frame shapes for Round faces',
      frames: ['Square', 'Rectangle', 'Cat-eye', 'Slightly angular wayfarer'],
      avoid: ['Round frames', 'Very soft circles', 'Oversized round shapes'],
    };
  }

  if (shape === 'oval') {
    return {
      ...common,
      headline: 'Best frame shapes for Oval faces',
      frames: ['Pilot', 'Rectangle', 'Cat-eye', 'Butterfly (medium size)'],
      avoid: ['Extremely tiny frames', 'Very narrow frames that stretch too long'],
    };
  }

  if (shape === 'straight') {
    return {
      ...common,
      headline: 'Best frame shapes for Straight/Oblong faces',
      frames: ['Rounded-rectangle', 'Soft square', 'Cat-eye', 'Butterfly (balanced width)'],
      avoid: ['Long thin frames', 'Very narrow vertical styles', 'Ultra-rectangular tall frames'],
    };
  }

  return {
    ...common,
    headline: 'Get your frame suggestions',
    frames: ['Run "Analyze with AI" to see personalized suggestions'],
    avoid: [],
  };
}

function GetFaceMatchingPage() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const [isRunning, setIsRunning] = useState(false);
  const [status, setStatus] = useState('Camera is off');
  const [faceShape, setFaceShape] = useState('Unknown');
  const [liveFaceStatus, setLiveFaceStatus] = useState('No analysis yet');
  const [hasDetectionSupport, setHasDetectionSupport] = useState(true);
  const [isMirrored, setIsMirrored] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const normalizedShape = normalizeFaceShape(faceShape);
  const recommendations = getFrameRecommendations(normalizedShape);

  async function stopCamera() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsRunning(false);
    setStatus('Camera is off');
    setLiveFaceStatus('No analysis yet');
  }

  async function startCamera() {
    try {
      setStatus('Requesting camera permission...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 640, height: 480 },
        audio: false,
      });

      if (!window.FaceDetector) {
        setHasDetectionSupport(false);
        setStatus(
          'Camera started in compatibility mode. Use "Analyze with AI" to detect face shape.'
        );
      } else {
        setHasDetectionSupport(true);
        setStatus('Detecting face...');
      }

      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setIsRunning(true);

      if (!window.FaceDetector) {
        return;
      }

      const detector = new window.FaceDetector({
        fastMode: true,
        maxDetectedFaces: 1,
      });

      timerRef.current = setInterval(async () => {
        if (!videoRef.current || videoRef.current.readyState < 2) {
          return;
        }

        try {
          const faces = await detector.detect(videoRef.current);

          if (!faces.length) {
            setStatus('No face detected. Center your face in camera.');
            setLiveFaceStatus('No face detected');
            return;
          }

          setStatus('Face detected successfully.');
          setLiveFaceStatus('Face detected');
        } catch (_error) {
          setStatus('Face detection failed. Please retry.');
          setLiveFaceStatus('Detection failed');
        }
      }, 700);
    } catch (_error) {
      setStatus('Could not access camera. Please allow camera permission.');
      setIsRunning(false);
    }
  }

  useEffect(() => () => stopCamera(), []);

  async function analyzeCurrentFrame() {
    if (!videoRef.current || videoRef.current.readyState < 2) {
      setStatus('Start camera first before AI analysis.');
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const width = video.videoWidth || 640;
    const height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    let imageDataUrl = '';

    if (window.FaceDetector) {
      try {
        const detector = new window.FaceDetector({
          fastMode: false,
          maxDetectedFaces: 1,
        });
        const faces = await detector.detect(video);

        if (faces.length) {
          const box = faces[0].boundingBox;
          const padX = box.width * 0.25;
          const padY = box.height * 0.35;
          const sx = clamp(Math.floor(box.x - padX), 0, width);
          const sy = clamp(Math.floor(box.y - padY), 0, height);
          const sw = clamp(Math.ceil(box.width + padX * 2), 1, width - sx);
          const sh = clamp(Math.ceil(box.height + padY * 2), 1, height - sy);

          canvas.width = sw;
          canvas.height = sh;
          ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
          imageDataUrl = canvas.toDataURL('image/jpeg', 0.92);
        }
      } catch (_error) {
        imageDataUrl = '';
      }
    }

    if (!imageDataUrl) {
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(video, 0, 0, width, height);
      imageDataUrl = canvas.toDataURL('image/jpeg', 0.92);
    }

    try {
      setAiLoading(true);
      const result = await analyzeFaceShape(imageDataUrl);
      setAiResult(result);
      if (result.shape) {
        setFaceShape(String(result.shape));
      }
      setStatus('AI analysis completed.');
    } catch (error) {
      setStatus(error.message || 'AI analysis failed.');
    } finally {
      setAiLoading(false);
    }
  }

  return (
    <section className="page-card face-match-page">
      <div className="face-match-hero">
        <p className="products-page__crumb">Home / Face Matching</p>
        <h1 className="page-card__title">Find the Perfect Frame for Your Face</h1>
        <p className="page-card__text">
          Open the camera, capture a frame, and let AI suggest the frame styles
          that suit your face shape.
        </p>
        <div className="face-match-steps">
          <span><em>1</em> Start your camera</span>
          <span><em>2</em> Analyze with AI</span>
          <span><em>3</em> Get frame suggestions</span>
        </div>
      </div>
      <div className="face-match-layout">
        <canvas ref={canvasRef} className="face-match-canvas" />
        <div className="face-match-video-wrap">
          <video
            ref={videoRef}
            className={`face-match-video ${isMirrored ? 'is-mirrored' : ''}`}
            autoPlay
            muted
            playsInline
          />
          {!isRunning && (
            <div className="face-match-overlay">
              <span className="face-match-overlay__icon">
                <Icon name="scan-face" size={30} />
              </span>
              <strong>Camera is off</strong>
              <p>Press &ldquo;Start Camera&rdquo; and center your face in the frame.</p>
            </div>
          )}
        </div>
        <div className="face-match-panel">
          <p className="face-match-status">{status}</p>
          <div className="face-match-rows">
            <div className="face-match-row">
              <span>AI Face Shape</span>
              <strong className="face-match-pill">{faceShape}</strong>
            </div>
            <div className="face-match-row">
              <span>Live Camera</span>
              <strong
                className={`face-match-pill ${
                  liveFaceStatus === 'Face detected' ? 'face-match-pill--ok' : ''
                }`}
              >
                {liveFaceStatus}
              </strong>
            </div>
          </div>
          {!hasDetectionSupport && (
            <p className="products-state">
              Built-in face detector is unavailable in this browser. AI analysis still works.
            </p>
          )}
          <div className="face-match-actions">
            <button
              type="button"
              className="face-btn face-btn--primary"
              onClick={startCamera}
              disabled={isRunning}
            >
              Start Camera
            </button>
            <button
              type="button"
              className="face-btn face-btn--neutral"
              onClick={stopCamera}
              disabled={!isRunning}
            >
              Stop Camera
            </button>
            <button
              type="button"
              className="face-btn face-btn--neutral"
              onClick={() => setIsMirrored((prev) => !prev)}
            >
              {isMirrored ? 'Unflip Camera' : 'Flip Camera'}
            </button>
            <button
              type="button"
              className="face-btn face-btn--primary"
              onClick={analyzeCurrentFrame}
              disabled={!isRunning || aiLoading}
            >
              {aiLoading ? 'Analyzing...' : 'Analyze with AI'}
            </button>
          </div>
          {aiResult && (
            <div className="face-match-ai-result">
              <div className="face-match-ai-result__header">
                <strong>{aiResult.shape}</strong>
                <span>{aiResult.confidence}% confidence</span>
              </div>
              <p>{aiResult.reason}</p>
              <p>{aiResult.description}</p>
            </div>
          )}

          <div className="frame-suggestions">
            <h2 className="frame-suggestions__title">{recommendations.headline}</h2>
            <p className="frame-suggestions__note">{recommendations.note}</p>

            <div className="frame-suggestions__section">
              <p className="frame-suggestions__label">Try these</p>
              <div className="frame-suggestions__chips">
                {recommendations.frames.map((f) => (
                  <span key={f} className="frame-chip">
                    {f}
                  </span>
                ))}
              </div>
            </div>

            {recommendations.avoid.length > 0 && (
              <div className="frame-suggestions__section">
                <p className="frame-suggestions__label frame-suggestions__label--avoid">
                  Avoid
                </p>
                <div className="frame-suggestions__chips">
                  {recommendations.avoid.map((f) => (
                    <span key={f} className="frame-chip frame-chip--avoid">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="frame-guide">
              <p className="frame-suggestions__label">Frame Shape Guide</p>
              <img
                src="/frame-shape-guide.png"
                alt="Frame Shape Guide"
                className="frame-guide__img"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default GetFaceMatchingPage;
